import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {SxProps, Theme} from '@mui/material/styles';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import {kit, radius} from './tokens';

interface ErrorCardProps {
  headline?: string;
  message?: string;
  onRetry: () => void;
  retryLabel?: string;
  sx?: SxProps<Theme>;
}

// Honest error state (web-sys-states.html #4 `.errcard`): an icon, a plain
// headline, one line of context, and a "Try again" button that re-runs the
// same fetch. Every list/detail fetch in the redesign renders this on
// failure instead of silently looking like an empty list.
const ErrorCard = ({
  headline = 'We could not load this',
  message = 'Something went wrong on our end. Check your connection and try again.',
  onRetry,
  retryLabel = 'Try again',
  sx,
}: ErrorCardProps) => (
  <Box
    sx={[
      {
        backgroundColor: '#FFFFFF',
        border: `1px solid ${kit.line}`,
        borderRadius: `${radius.lg}px`,
        padding: '40px 32px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
      },
      ...(Array.isArray(sx) ? sx : [sx]),
    ]}>
    <Box
      sx={{
        width: 48,
        height: 48,
        borderRadius: `${radius.pill}px`,
        backgroundColor: kit.dangerTint,
        color: kit.danger,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '16px',
      }}>
      <ErrorOutlineRounded sx={{fontSize: 24}} />
    </Box>
    <Typography
      sx={{
        fontFamily: 'inherit',
        fontSize: 17,
        fontWeight: 700,
        color: kit.ink,
      }}>
      {headline}
    </Typography>
    <Typography
      sx={{
        marginTop: '6px',
        maxWidth: 380,
        fontSize: 14,
        lineHeight: 1.5,
        color: kit.slate,
      }}>
      {message}
    </Typography>
    <Box
      component="button"
      type="button"
      onClick={onRetry}
      sx={{
        appearance: 'none',
        marginTop: '20px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '7px',
        height: 38,
        padding: '0 16px',
        borderRadius: `${radius.sm}px`,
        border: `1.5px solid ${kit.line}`,
        backgroundColor: '#FFFFFF',
        color: kit.inkSoft,
        fontFamily: 'inherit',
        fontSize: 13.5,
        fontWeight: 600,
        cursor: 'pointer',
        '&:hover': {backgroundColor: kit.mist},
      }}>
      <ReplayRounded sx={{fontSize: 15}} />
      {retryLabel}
    </Box>
  </Box>
);

export default ErrorCard;
